import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Route as RouteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';

const RUTA_STATUSES = [
  { id: "activa", name: "Activa", color: "success" },
  { id: "inactiva", name: "Inactiva", color: "default" }
];

export default function CrearRutaDialog({ open, onClose, onConfirm, ruta = null }) {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    zona_geografica: '',
    id_cobrador_asignado: '',
    estado: 'activa'
  });

  const [cobradores, setCobradores] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [messageAlert, setMessageAlert] = useState('');

  const isEdit = !!ruta;

  useEffect(() => {
    if (open) {
      fetchCobradores();
      
      if (ruta) {
        setFormData({
          nombre: ruta.nombre || '',
          descripcion: ruta.descripcion || '',
          zona_geografica: ruta.zona_geografica || '',
          id_cobrador_asignado: ruta.id_cobrador_asignado || '',
          estado: ruta.estado || 'activa'
        });
      } else {
        setFormData({
          nombre: '',
          descripcion: '',
          zona_geografica: '',
          id_cobrador_asignado: '',
          estado: 'activa'
        });
      }
      setErrors({});
    }
  }, [open, ruta]);

  const fetchCobradores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cobradores/disponibles/${localStorage.getItem('idcompany')}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCobradores(data);
      }
    } catch (error) {
      console.error('Error fetching cobradores:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombre || !formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setMessageAlert('Por favor, corrige los errores en el formulario.');
      setOpenAlert(true);
      return;
    }

    setLoading(true);
    try {
      const url = isEdit 
        ? `${process.env.REACT_APP_SERVER_URL}/api/rutas/${ruta.id}`
        : `${process.env.REACT_APP_SERVER_URL}/api/rutas`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const requestData = {
        ...formData,
        idcompany: localStorage.getItem('idcompany'),
        created_by: localStorage.getItem('id')
      };

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        setMessageAlert(isEdit ? 'Ruta actualizada exitosamente.' : 'Ruta creada exitosamente.');
        setOpenAlert(true);
        onConfirm(isEdit ? { ...ruta, ...formData } : result);
        onClose();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Error al procesar la solicitud');
      }
    } catch (error) {
      setMessageAlert(error.message || 'Error al procesar la solicitud.');
      setOpenAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      zona_geografica: '',
      id_cobrador_asignado: '',
      estado: 'activa'
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
            >
              {isEdit ? <EditIcon sx={{ fontSize: 24 }} /> : <RouteIcon sx={{ fontSize: 24 }} />}
            </Box>
            <Typography variant="h6" fontWeight="bold">
              {isEdit ? 'Editar Ruta' : 'Nueva Ruta'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            {/* Nombre */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Nombre de la Ruta *
              </Typography>
              <TextField
                fullWidth
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                error={!!errors.nombre}
                helperText={errors.nombre}
                placeholder="Ej: Ruta Centro, Zona Norte"
              />
            </Grid>

            {/* Zona Geográfica */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Zona Geográfica
              </Typography>
              <TextField
                fullWidth
                value={formData.zona_geografica}
                onChange={(e) => handleInputChange('zona_geografica', e.target.value)}
                placeholder="Ej: Centro de la ciudad, Zona Norte"
              />
            </Grid>

            {/* Descripción */}
            <Grid item size={{ xs: 12 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Descripción
              </Typography>
              <TextField
                fullWidth
                multiline
                value={formData.descripcion}
                onChange={(e) => handleInputChange('descripcion', e.target.value)}
                placeholder="Descripción detallada de la ruta..."
              />
            </Grid>

            {/* Cobrador Asignado */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Cobrador Asignado
              </Typography>
              <FormControl fullWidth>
                <Select
                  sx={{
                    backgroundColor: 'white'
                  }}
                  value={formData.id_cobrador_asignado}
                  onChange={(e) => handleInputChange('id_cobrador_asignado', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Seleccionar cobrador</em>
                  </MenuItem>
                  {cobradores.map((cobrador) => (
                    <MenuItem key={cobrador.id} value={cobrador.id}>
                      {cobrador.nombre} - {cobrador.identificacion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Estado */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" fontWeight={600} ml={1} mb={1}>
                Estado
              </Typography>
              <FormControl fullWidth>
                <Select
                  sx={{
                    backgroundColor: 'white'
                  }}
                  value={formData.estado}
                  onChange={(e) => handleInputChange('estado', e.target.value)}
                >
                  {RUTA_STATUSES.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip 
                          label={status.name} 
                          color={status.color} 
                          size="small"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button onClick={handleClose} variant="outlined" sx={{ fontWeight: 'bold' }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            color="primarybutton"
            sx={{ 
              fontWeight: 'bold'
            }}
          >
            {loading ? (isEdit ? 'Actualizando...' : 'Creando...') : (isEdit ? 'Actualizar Ruta' : 'Crear Ruta')}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessageDialog 
        message={messageAlert} 
        title="" 
        severity="success" 
        color="success" 
        open={openAlert} 
        handleClose={() => setOpenAlert(false)}
      />
    </>
  );
}
